import React from "react";
import { CheckCircle2, Info, BookOpen, Heart } from "lucide-react";
import SidebarContents from "../../components/SidebarContents";
import SectionContents from "../../components/SectionContents";

const sections = [
  {
    sectionId: "commitment",
    sectionTitle: "1. Our Commitment",
    sectionBody: (
      <div className="space-y-4 text-sm leading-relaxed">
        <p>
          At Hyvid, your safety and wellbeing matter — whether you're working from a home office in Manila or anywhere else in the Philippines. This policy sets out how we keep you safe, healthy, and supported while you work remotely.
        </p>
        <p>
          This policy complies with the Occupational Safety and Health Standards Act (Republic Act No. 11058), DOLE Department Order No. 252-25 (Series of 2025), the Telecommuting Act (Republic Act No. 11165), DOLE Department Order No. 237-22, and the Philippine Mental Health Act (Republic Act No. 11036).
        </p>
      </div>
    ),
  },

  {
    sectionId: "who",
    sectionTitle: "2. Who This Policy Applies To",
    sectionBody: (
      <div className="space-y-4 text-sm leading-relaxed">
        <p>
          Everyone employed by Hyvid in the Philippines, regardless of role or employment status, including probationary employees. Because Hyvid operates as a fully remote company, the "workplace" means wherever you perform your work — your home, a co-working space, or another agreed location.
        </p>
      </div>
    ),
  },

  {
    sectionId: "workspace",
    sectionTitle: "3. Your Home Workspace",
    sectionBody: (
      <div className="space-y-4 text-sm leading-relaxed">
        <div className="mb-4">
          <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
            3.1 Setting Up Safely
          </h3>

          <div className="pl-4 text-sm leading-relaxed space-y-1.5">
            <p>You're responsible for maintaining a safe, clean, and functional workspace at home. Here's what that looks like in practice:</p>

            <ul className="list-none space-y-1.5 pl-2">
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                <span>Your desk, chair, and screen should be set up so you're comfortable — Hyvid will provide ergonomics guidance to help with this.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                <span>Your workspace should be well-lit and free from trip hazards, loose cables, or anything that could cause injury.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                <span>Your internet connection and equipment should be kept secure and in good working order.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                <span>Keep your workspace reasonably tidy and free from distractions that affect your concentration or safety.</span>
              </li>
            </ul>

            <p>
              Hyvid will send you an ergonomics guide when you start, and your manager can arrange a virtual workspace check-in if you'd like help getting set up properly.
            </p>
          </div>
        </div>

        <div className="mb-4">
          <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
            3.2 Workspace Assessment
          </h3>

          <div className="pl-4 text-sm leading-relaxed space-y-1.5">
            <p>
              When you join Hyvid, you'll complete a short home workspace self-assessment via HyvidOne. This helps us confirm your setup meets basic safety requirements and flags anything we can help you address before you start work. We may also ask for a photo or short video walkthrough.
            </p>

            <div className="flex gap-3 p-3 rounded-lg border bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700">
              <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5"/>
              <div className="text-sm">
                We'll do this assessment again if you move to a new workspace, and periodically as part of our OSH program review.
              </div>
            </div>
          </div>
        </div>
      </div>
    ),
  },

  {
    sectionId: "wellbeing",
    sectionTitle: "4. Your Health & Wellbeing",
    sectionBody: (
      <div className="space-y-4 text-sm leading-relaxed">
        <div>
          <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
            4.1 Mental Health
          </h3>

          <p>Remote work has real benefits — but it can also blur the line between work and personal life. We take mental health seriously at Hyvid.</p>

          <p>In line with DOLE DO 252-25 and RA 11036 (Philippine Mental Health Act):</p>

          <ul className="list-none space-y-1.5 pl-2">
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
              <span>You have the right to disconnect outside of your agreed working hours. Hyvid will not expect you to be available around the clock.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
              <span>Your working hours will be clearly defined in your employment agreement and confirmed by your manager.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
              <span>If you're struggling — with workload, stress, or anything personal — reach out to People & Culture. We're here to help, not judge.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
              <span>Hyvid's mental health support resources are available via HyvidOne.</span>
            </li>
          </ul>
        </div>

        <div>
          <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
            4.2 Fatigue and Workload
          </h3>
          <p>If you feel your workload is consistently unmanageable, or that you're regularly working beyond your agreed hours, please raise it with your manager or People & Culture. We want to know so we can fix it.</p>
        </div>

        <div>
          <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
            4.3 Physical Health
          </h3>

          <ul className="list-none space-y-1.5 pl-2">
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
              <span>Take your two paid 15-minute breaks and your unpaid 60-minute meal break every day.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
              <span>Move around regularly — a short walk or stretch goes a long way.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
              <span>Use your sick leave when you're unwell. Working through illness helps no one.</span>
            </li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    sectionId: "training",
    sectionTitle: "5. OSH Training",
    sectionBody: (
      <div className="space-y-4 text-sm leading-relaxed">
        <div className="mb-4">
          <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
            5.1 Mandatory 8-Hour OSH Seminar
          </h3>

          <div className="pl-4 text-sm leading-relaxed space-y-1.5">
            <p>
              All Hyvid employees are required to complete the DOLE-mandated 8-hour Occupational Safety and Health seminar. This is a legal requirement under RA 11058.
            </p>

            <p>What you need to know:</p>

            <ul className="list-none space-y-1.5 pl-2">
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                <span>You'll be enrolled in an accredited online course during your first week. Hyvid will cover the cost.</span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                <span>The course takes 8 hours and can be completed self-paced from any device.</span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                <span>You must complete it within your first two weeks of employment.</span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                <span>Once done, upload your certificate of completion to HyvidOne.</span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                <span>A valid certificate is required for your personnel file — it will be checked during DOLE compliance audits.</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mb-4">
          <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
            5.2 What the Training Covers
          </h3>

          <div className="pl-4 text-sm leading-relaxed space-y-1.5">
            <ul className="list-none space-y-1.5 pl-2">
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                <span>Identifying workplace hazards and assessing risk</span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                <span>Control measures and how to apply them</span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                <span>Emergency preparedness and what to do in a crisis</span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                <span>Ergonomics and computer vision syndrome prevention</span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                <span>Your rights and responsibilities under OSH law</span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                <span>How to report a hazard or incident</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mb-4">
          <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
            5.3 Ongoing Safety Awareness
          </h3>

          <div className="pl-4 text-sm leading-relaxed space-y-1.5">
            <p>
              Beyond the mandatory seminar, Hyvid will share regular safety tips, reminders, and updates through HyvidOne — especially when regulations change or new guidance is issued by DOLE.
            </p>
          </div>
        </div>
      </div>
    )
  },

  {
    sectionId: "reporting",
    sectionTitle: "6. Reporting Hazards and Incidents",
    sectionBody: (
      <div className="space-y-4 text-sm leading-relaxed">

        <div className="mb-4">
          <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
            6.1 If Something Feels Unsafe
          </h3>

          <div className="pl-4 text-sm leading-relaxed space-y-1.5">
            <p>
              You have the right to raise any safety concern without fear of being penalised for it. If your workspace develops a hazard — a broken chair, a persistent electrical issue, anything that affects your ability to work safely — tell your manager or People &amp; Culture straight away.
            </p>
          </div>
        </div>

        <div className="mb-4">
          <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
            6.2 If You Have a Work-Related Injury or Illness
          </h3>

          <div className="pl-4 text-sm leading-relaxed space-y-1.5">
            <p>
              If you're injured or become ill as a result of your work, even while working from home, you must:
            </p>

            <ul className="list-none space-y-1.5 pl-2">
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                <span>Report it to your manager and People &amp; Culture as soon as possible on the day it happens, or as soon as you are able.</span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                <span>Log the incident in HyvidOne using the incident report form.</span>
              </li>

              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                <span>Seek medical attention if needed and keep any records or receipts.</span>
              </li>
            </ul>

            <div className="flex gap-3 p-3 rounded-lg border bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700">
              <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />

              <div className="text-sm">
                Work-related injuries sustained during agreed working hours at your agreed workspace are covered under the Employees' Compensation Program (ECC). Hyvid will assist you with any claims.
              </div>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
            6.3 Right to Refuse Unsafe Work
          </h3>

          <div className="pl-4 text-sm leading-relaxed space-y-1.5">
            <div className="flex gap-3 p-3 rounded-lg border bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-700">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />

              <div className="text-sm">
                You can refuse to perform work that poses an imminent and serious danger to your safety or health. You will not be penalised for doing so. Raise the concern with your manager immediately so we can resolve it.
              </div>
            </div>
          </div>
        </div>

      </div>
    )
  },

  {
    sectionId: "program",
    sectionTitle: "7. Hyvid's OSH Program",
    sectionBody: (
      <div className="space-y-4 text-sm leading-relaxed">
        <p>
          As required by DOLE, Hyvid maintains a formal OSH Program that covers all employees regardless of work location. The program includes:
        </p>

        <ul className="list-none space-y-1.5 pl-2">
          <li className="flex items-start gap-2">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
            <span>A designated Safety Officer responsible for OSH compliance across the company.</span>
          </li>

          <li className="flex items-start gap-2">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
            <span>An OSH Committee that meets regularly to review hazards, incidents, and program effectiveness.</span>
          </li>

          <li className="flex items-start gap-2">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
            <span>Mandatory training and induction for all new employees.</span>
          </li>

          <li className="flex items-start gap-2">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
            <span>Periodic virtual workspace checks.</span>
          </li>

          <li className="flex items-start gap-2">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
            <span>Annual review and update of this policy and the broader OSH Program.</span>
          </li>

          <li className="flex items-start gap-2">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
            <span>Submission of required OSH reports to DOLE.</span>
          </li>
        </ul>

        <div className="flex gap-3 p-3 rounded-lg border bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700">
          <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5"/>
          <div className="text-sm">
            The OSH Program document is available on request from People &amp; Culture.
          </div>
        </div>
      </div>
    )
  },
  {
  sectionId: "responsibilities",
  sectionTitle: "8. Responsibilities",
  sectionBody: (
    <div className="space-y-4 text-sm leading-relaxed">
      <div className="grid sm:grid-cols-2 gap-4">

        <div className="rounded-xl border p-4 dark-mode-card shadow-sm">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-purple-100">
            <BookOpen className="w-4 h-4 text-purple-500" />
            <h4 className="font-bold text-sm title-text">Hyvid's responsibilities</h4>
          </div>

          <ul className="list-none space-y-1.5 pl-2">
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>Provide a safe working environment and the tools, guidance, and training to make that possible.</span>
            </li>

            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>Maintain and update the OSH Program in line with legal requirements.</span>
            </li>

            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>Respond promptly to all reported hazards and incidents.</span>
            </li>

            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>Support employees' mental health and physical wellbeing.</span>
            </li>

            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>Ensure no employee is penalised for raising a safety concern.</span>
            </li>
          </ul>
        </div>

        <div className="rounded-xl border p-4 dark-mode-card shadow-sm">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-emerald-100">
            <Heart className="w-4 h-4 text-emerald-500" />
            <h4 className="font-bold text-sm title-text">Your responsibilities</h4>
          </div>

          <ul className="list-none space-y-1.5 pl-2">
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>Complete your mandatory OSH seminar and any other required training.</span>
            </li>

            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>Maintain your home workspace in a safe condition.</span>
            </li>

            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>Report hazards, near-misses, and injuries promptly.</span>
            </li>

            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>Follow Hyvid's safety guidelines and any instructions from your manager related to health and safety.</span>
            </li>

            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>Take care of your own wellbeing and raise concerns early, before they become bigger problems.</span>
            </li>
          </ul>
        </div>

      </div>
    </div>
  ),
},

  {
    sectionId: "breaches",
    sectionTitle: "9. Breaches of This Policy",
    sectionBody: (
      <div id="breaches" className="mb-10 scroll-mt-6 text-sm leading-relaxed">
        <p>
          Wilful disregard for safety requirements, failure to complete mandatory training, or providing false information about a workplace incident are serious matters. Depending on the circumstances, these may result in disciplinary action in line with Hyvid's disciplinary framework.
        </p>
      </div>
    ),
  },

  {
    sectionId: "questions",
    sectionTitle: "10. Questions",
    sectionBody: (
      <div id="questions" className="mb-10 scroll-mt-6 text-sm leading-relaxed">
        <p>
          If you have any questions about this policy or your workspace setup, reach out to People &amp; Culture at any time via HyvidOne.
        </p>
        <div className="mt-8 p-4 rounded-xl border bg-gray-100 border-gray-200 dark:bg-gray-800/20 dark:border-gray-700">
          <p className="text-xs muted-text italic">
            <strong>Legal references:</strong> Occupational Safety and Health Standards Act 
            (RA 11058), DOLE Department Order No. 252-25 (Series of 2025), Telecommuting Act (RA 11165),
             DOLE Department Order No. 237-22, Philippine Mental Health Act (RA 11036).
          </p>
        </div>
      </div>
    ),
  },
];

const OccupationalSafetyContents = () => {
  return (
    <div  className="flex items-start">
      <SidebarContents
        sections={sections} 
      />
      <div  className="flex-1 p-6 space-y-6 max-w-4xl">
        {sections.map((section) => (
          <SectionContents
            key={section.sectionId}
            sectionId={section.sectionId}
            sectionTitle={section.sectionTitle}
            sectionBody={section.sectionBody}
          />
        ))}
      </div>
    </div>
  );
};

export default OccupationalSafetyContents;